<!-- Copyright: Seán I. O'Donoghue -->
<!-- The image collections' one header (Photos, Posters, Videos; Seán, 2026-10-26), in the -->
<!-- Videos page's look (2026-10-21): the heading, linking back to the whole collection, its -->
<!-- count beside it, and the page's filters on the right (the slot), all one family of -->
<!-- controls: neutral and the same height, a chosen one green -->
<template>
  <nav class="pageHeader">
    <!-- before the heading: a page's way back, when it has one (the poster gallery's arrow to
         the year it came from) -->
    <slot name="before" />
    <h3>
      <router-link :to="to" class="headingLink">
        {{ title }}
        <Count v-if="count !== ''" :count :total />
      </router-link>
    </h3>
    <div id="right" :class="controlsClass">
      <slot />
    </div>
  </nav>
</template>

<script setup>
import Count from "@/components/Count.vue";

defineProps({
  title: {type: String, required: true},
  to: {type: String, required: true}, // the whole collection, unfiltered
  count: {type: [String, Number], default: ""}, // e.g. 1076, or 47 of total while filtered
  total: {type: [String, Number], default: ""}, // the whole collection, while filtered
  controlsClass: {type: [String, Object, Array], default: ""}
});
</script>

<style scoped>
nav {
  display: flex;
  align-items: center;
  /* the filter controls' colours and size, for every control in the slot (2026-10-21, Seán) */
  --filter-height: 2.5rem;
  --filter-weight: 600; /* the controls' labels: a little bold */
  --filter-text: var(--filter-control-text);
  --filter-border: var(--filter-control-border);
  --filter-background: var(--filter-control-background);
}
h3 {
  display: inline;
  margin-bottom: 4px;
}
/* the heading links back to the collection, but looks like a heading */
.headingLink {
  color: inherit;
  text-decoration: none;
  margin-right: 20px;
}
/* the count (Count.vue), a space after the heading; its middle on the heading's capitals' middle, not on its baseline (2026-10-21, Seán) */
h3 .count {
  margin-left: 0.25em;
  position: relative;
  top: -0.3em;
}
.medium .count,
.narrow .count {
  display: none; /* mobile first */
}
#right {
  display: flex;
  flex-wrap: wrap; /* more filters than fit beside the heading on a phone */
  justify-content: flex-end;
  align-items: flex-start; /* prevent from changing height */
  gap: 0.8rem;
  /* the buttons' middle on the heading's capitals' middle (2026-10-21, Seán): 3px higher */
  position: relative;
  top: -3px;
  margin-left: auto;
}
#right :deep(button:not([role="tab"])) {
  min-height: var(--filter-height);
  border-width: 1px;
  font-weight: var(--filter-weight);
}
#right :deep(button:not(.tag.selected):not(.active):not([role="tab"])) {
  color: var(--filter-text);
  background: var(--filter-background);
  border-color: var(--filter-border);
}
#right :deep(button.tag.selected),
#right :deep(button.active) {
  border-color: var(--brand-color);
}
/* a segmented control (App/SegmentedControl.vue): the same height as the buttons, its track and
   capsule its own */
#right :deep(.toggle) {
  min-height: var(--filter-height);
  box-sizing: border-box;
}
/* one gap between controls: the header's (Dropdown.vue adds its own between two menus) */
#right :deep(.container + .container) {
  margin-left: 0;
}
/* an open menu: as its closed button */
#right :deep(menu) {
  background-color: var(--filter-background) !important;
  border-color: var(--filter-border);
}
#right :deep(menu .item a) {
  color: var(--filter-text);
}
/* the options (Dropdown.vue) and their lines in the controls' text colour, not the faint
   --text-color-subtle (dark mode: 1.97:1 on the menu; Seán, 2026-10-26); the title, the chosen
   option and hover keep their own */
#right :deep(menu > li:not(.title):not(.active)) {
  color: var(--filter-text);
}
</style>
